import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getSchoolAssetUrl, getProfileAssetUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  IndianRupee,
  CreditCard,
  ClipboardList,
  User,
  FileText,
  Camera,
  Calendar,
  Save,
  Edit,
  Users,
  Send,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';

// Lazy-load html2pdf so receipt download works without blocking
let html2pdfPromise = null;
function getHtml2pdf() {
  if (!html2pdfPromise) {
    html2pdfPromise = import('html2pdf.js').then((m) => m.default);
  }
  return html2pdfPromise;
}

const StudentProfile = ({ editMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [feeMonths, setFeeMonths] = useState(null);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);
  const [feeStructure, setFeeStructure] = useState([]);
  const [payFeeDialogOpen, setPayFeeDialogOpen] = useState(false);
  const [payFeeForm, setPayFeeForm] = useState({
    student_id: '',
    amount: '',
    discount: '0',
    payment_method: 'cash',
    remarks: '',
    month_label: '',
    fee_month: '', // YYYY-MM for which month the fee is for
    payment_date: new Date().toISOString().split('T')[0], // Date when payment was made (can be backdated)
  });
  const [resendingCreds, setResendingCreds] = useState(false);
  const photoInputRef = React.useRef(null);

  const isAdmin = user?.role_slug === 'super_admin' || user?.role_slug === 'school_admin';

  const handleResendCredentials = async () => {
    const uid = profile?.student?.user_id;
    if (!uid) return;
    setResendingCreds(true);
    try {
      const res = await api.post('/auth/resend-credentials', { user_id: uid });
      const msg = res?.data?.message || 'New login credentials have been emailed to the user.';
      toast({ title: 'Credentials sent', description: msg });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.message || 'Could not send credentials', variant: 'destructive' });
    } finally {
      setResendingCreds(false);
    }
  };

  useEffect(() => {
    if (id) fetchProfile();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`fees/student/${id}/months`)
      .then((res) => setFeeMonths(res.data.data?.months ?? []))
      .catch(() => setFeeMonths([]));
  }, [id]);

  useEffect(() => {
    if (!profile?.student?.class_id) return;
    const params = { class_id: profile.student.class_id };
    if (user?.school_id != null) params.school_id = user.school_id;
    api.get('/fees/structure', { params })
      .then((res) => setFeeStructure(res.data.data || []))
      .catch(() => setFeeStructure([]));
  }, [profile?.student?.class_id, user?.school_id]);

  useEffect(() => {
    if (!editMode) return;
    api.get('classes')
      .then((res) => setClasses(res.data.data || []))
      .catch(() => setClasses([]));
  }, [editMode]);

  useEffect(() => {
    if (!editMode || !formData?.class_id) {
      setSections([]);
      return;
    }
    api.get(`classes/${formData.class_id}/sections`)
      .then((res) => setSections(res.data.data || []))
      .catch(() => setSections([]));
  }, [editMode, formData?.class_id]);

  // When navigating from profile view to edit (same id), profile is already loaded but formData was never set. Sync formData from profile.
  useEffect(() => {
    if (!editMode || !profile?.student) return;
    setFormData((prev) => {
      if (prev != null) return prev;
      const s = profile.student;
      return {
        first_name: s.first_name || '',
        last_name: s.last_name || '',
        phone: s.phone || '',
        class_id: s.class_id?.toString() || '',
        section_id: s.section_id?.toString() || '',
        roll_no: s.roll_no || '',
        date_of_birth: (s.date_of_birth || '').toString().slice(0, 10),
        admission_date: (s.admission_date || '').toString().slice(0, 10),
        gender: s.gender || '',
        blood_group: s.blood_group || '',
        address: s.address || '',
        city: s.city || '',
        state: s.state || '',
        pincode: s.pincode || '',
        status: s.status || 'active',
        guardian_name: s.guardian_name || '',
        guardian_phone: s.guardian_phone || '',
        guardian_email: s.guardian_email || '',
      };
    });
  }, [editMode, profile]);

  const fetchProfile = async () => {
    try {
      const response = await api.get(`students/${id}/profile`);
      const data = response.data.data;
      setProfile(data);
      if (editMode && data?.student) {
        const s = data.student;
        setFormData({
          first_name: s.first_name || '',
          last_name: s.last_name || '',
          phone: s.phone || '',
          class_id: s.class_id?.toString() || '',
          section_id: s.section_id?.toString() || '',
          roll_no: s.roll_no || '',
          date_of_birth: (s.date_of_birth || '').toString().slice(0, 10),
          admission_date: (s.admission_date || '').toString().slice(0, 10),
          gender: s.gender || '',
          blood_group: s.blood_group || '',
          address: s.address || '',
          city: s.city || '',
          state: s.state || '',
          pincode: s.pincode || '',
          status: s.status || 'active',
          guardian_name: s.guardian_name || '',
          guardian_phone: s.guardian_phone || '',
          guardian_email: s.guardian_email || '',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to load student profile',
        variant: 'destructive',
      });
      navigate('/students');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData || !id) return;
    setSaving(true);
    try {
      await api.put(`students/${id}`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || null,
        class_id: formData.class_id ? parseInt(formData.class_id, 10) : null,
        section_id: formData.section_id ? parseInt(formData.section_id, 10) : null,
        roll_no: formData.roll_no || null,
        date_of_birth: formData.date_of_birth || null,
        admission_date: formData.admission_date || null,
        gender: formData.gender || null,
        blood_group: formData.blood_group || null,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        pincode: formData.pincode || null,
        status: formData.status || null,
        guardian_name: formData.guardian_name || null,
        guardian_phone: formData.guardian_phone || null,
        guardian_email: formData.guardian_email || null,
      });
      toast({ title: 'Success', description: 'Student updated successfully' });
      navigate(`/students/${id}`);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to update student',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return d;
    }
  };

  const formatCurrency = (n) => {
    const num = Number(n);
    if (isNaN(num)) return '—';
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const calculateMonthlyFeeAmount = () => {
    if (!feeStructure || !feeStructure.length) return 0;
    // Sum all monthly fees (fees that recur every month)
    const monthlyFees = feeStructure
      .filter((fs) => fs.fee_type_frequency === 'monthly')
      .reduce((sum, fs) => sum + Number(fs.amount || 0), 0);
    return monthlyFees;
  };

  const handleOpenPayFee = async (month) => {
    if (!id || !profile?.student?.class_id) return;
    
    // Ensure fee structure is loaded
    let structure = feeStructure;
    if (!structure || structure.length === 0) {
      try {
        const params = { class_id: profile.student.class_id };
        if (user?.school_id != null) params.school_id = user.school_id;
        const res = await api.get('/fees/structure', { params });
        structure = res.data.data || [];
        setFeeStructure(structure);
      } catch (err) {
        console.error('Failed to load fee structure', err);
        structure = [];
      }
    }
    
    // Calculate amount from fee structure
    const monthlyAmount = structure
      .filter((fs) => fs.fee_type_frequency === 'monthly')
      .reduce((sum, fs) => sum + Number(fs.amount || 0), 0);
    
    setPayFeeForm({
      student_id: id,
      amount: monthlyAmount > 0 ? String(monthlyAmount) : '',
      discount: '0',
      payment_method: 'cash',
      remarks: `Fee for ${month.month_label}`,
      month_label: month.month_label,
      fee_month: month.year_month || '', // so backend marks this month as paid
      payment_date: new Date().toISOString().split('T')[0], // Default to today, can be changed for backdating
    });
    setPayFeeDialogOpen(true);
  };

  const handlePayFee = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        student_id: payFeeForm.student_id,
        amount: payFeeForm.amount,
        discount: payFeeForm.discount || '0',
        payment_method: payFeeForm.payment_method,
        remarks: payFeeForm.remarks,
        payment_date: payFeeForm.payment_date || new Date().toISOString().split('T')[0], // Include payment date for backdating
      };
      if (payFeeForm.fee_month && payFeeForm.fee_month.match(/^\d{4}-\d{2}$/)) {
        payload.fee_month = payFeeForm.fee_month;
      }
      const response = await api.post('/fees/collect', payload);
      const monthLabel = payFeeForm.month_label || (payFeeForm.fee_month ? new Date(payFeeForm.fee_month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '');
      toast({
        title: 'Success',
        description: `Fee for ${monthLabel || 'selected month'} collected. Receipt: ${response.data.data.receipt_no}`,
      });
      setPayFeeDialogOpen(false);
      setPayFeeForm({
        student_id: '',
        amount: '',
        discount: '0',
        payment_method: 'cash',
        remarks: '',
        month_label: '',
        fee_month: '',
        payment_date: new Date().toISOString().split('T')[0],
      });
      fetchProfile();
      api.get(`fees/student/${id}/months`)
        .then((res) => setFeeMonths(res.data.data?.months ?? []))
        .catch(() => setFeeMonths([]));
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to collect fee',
        variant: 'destructive',
      });
    }
  };

  // A5 = half A4 (148mm x 210mm). Content width ~132mm with 8mm margin ≈ 500px at 96dpi so receipt fills the sheet.
  const receiptWidthPx = 500;
  const receiptStyles = `
* { box-sizing: border-box; }
.receipt-pdf-wrap { font-family: Arial, Helvetica, sans-serif; font-size: 9px; color: #1f2937; padding: 12px; background: #ffffff; width: ${receiptWidthPx}px; }
.receipt { width: 100%; margin: 0 auto; border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; background: #ffffff; }
.header { background: #1e40af; color: #ffffff; text-align: center; padding: 12px 14px; }
.header .school-logo { max-height: 44px; max-width: 100px; width: auto; height: auto; object-fit: contain; display: block; margin: 0 auto 6px auto; background: #ffffff; border-radius: 4px; padding: 2px; }
.header h1 { margin: 0 0 4px 0; font-size: 15px; font-weight: bold; letter-spacing: 0.3px; color: #ffffff; }
.header .school-address { margin: 2px 0; font-size: 9px; color: #ffffff; line-height: 1.3; }
.header .school-phone { margin: 2px 0 0 0; font-size: 9px; color: #ffffff; }
.receipt-title { text-align: center; font-size: 12px; font-weight: bold; color: #1e40af; padding: 10px 14px; border-bottom: 1px solid #e5e7eb; background: #f8fafc; }
.content { padding: 12px 14px; background: #ffffff; }
.section { margin-bottom: 12px; }
.section:last-of-type { margin-bottom: 0; }
.section-title { font-size: 8px; font-weight: bold; color: #6b7280; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; padding-bottom: 2px; border-bottom: 1px solid #e5e7eb; }
table { width: 100%; border-collapse: collapse; background: #ffffff; font-size: 10px; }
th, td { padding: 5px 10px; text-align: left; border-bottom: 1px solid #e5e7eb; vertical-align: top; color: #1f2937; background: #ffffff; }
th { font-weight: 600; color: #4b5563; width: 38%; }
.amount-table th { width: 50%; }
.amount-table td { text-align: right; font-weight: 500; color: #1f2937; }
.total-row { background: #eff6ff !important; border-bottom: none !important; font-weight: bold; font-size: 11px; color: #1e40af !important; }
.total-row th, .total-row td { padding: 8px 10px; border-top: 2px solid #1e40af; color: #1e40af !important; background: #eff6ff !important; }
.footer { text-align: center; padding: 10px 14px; font-size: 8px; color: #6b7280; border-top: 1px solid #e5e7eb; background: #f9fafb; }
.footer .generated { margin-top: 2px; color: #6b7280; }
`;

  const buildReceiptBodyContent = (p) => {
    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    const studentName = [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || '—';
    const paymentDate = p.payment_date
      ? new Date(p.payment_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
      : '—';
    const amt = (n) => formatCurrency(n);
    const paymentMethod = (p.payment_method || '—')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const status = (p.status || 'paid').charAt(0).toUpperCase() + (p.status || 'paid').slice(1);
    const amount = Number(p.amount) || 0;
    const discount = Number(p.discount) || 0;
    const lateFee = Number(p.late_fee) || 0;
    const total = Number(p.total_amount) ?? amount;
    const showDiscount = discount > 0;
    const showLateFee = lateFee > 0;
    const generatedAt = new Date().toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    let amountRows = `<tr><th>Fee Amount</th><td>${amt(amount)}</td></tr>`;
    if (showDiscount) amountRows += `<tr><th>Discount</th><td>-${amt(discount)}</td></tr>`;
    if (showLateFee) amountRows += `<tr><th>Late Fee</th><td>${amt(lateFee)}</td></tr>`;
    amountRows += `<tr class="total-row"><th>TOTAL AMOUNT</th><td>${amt(total)}</td></tr>`;

    let remarksRow = '';
    if (p.remarks && String(p.remarks).trim()) {
      remarksRow = `<tr><th>Remarks</th><td>${esc(p.remarks)}</td></tr>`;
    }

    const schoolAddress = p.school_address ? `<p class="school-address">${esc(p.school_address)}</p>` : '';
    const schoolPhone = p.school_phone ? `<p class="school-phone">Ph: ${esc(p.school_phone)}</p>` : '';
    const logoUrl = getSchoolAssetUrl(p.school_logo, p.school_id);
    const logoImg = logoUrl ? `<img class="school-logo" src="${esc(logoUrl)}" alt="School logo" crossorigin="anonymous" />` : '';

    return `<style>${receiptStyles}</style>
<div class="receipt-pdf-wrap"><div class="receipt">
  <div class="header">
    ${logoImg}
    <h1>${esc(p.school_name || 'School')}</h1>
    ${schoolAddress}
    ${schoolPhone}
  </div>
  <div class="receipt-title">FEE PAYMENT RECEIPT</div>
  <div class="content">
    <div class="section">
      <div class="section-title">Payment Details</div>
      <table>
        <tr><th>Receipt No</th><td>${esc(p.receipt_no)}</td></tr>
        <tr><th>Date</th><td>${paymentDate}</td></tr>
        <tr><th>Student Name</th><td>${esc(studentName)}</td></tr>
        <tr><th>Admission No</th><td>${esc(p.admission_no)}</td></tr>
        <tr><th>Payment Method</th><td>${esc(paymentMethod)}</td></tr>
        <tr><th>Status</th><td>${esc(status)}</td></tr>
        ${remarksRow}
      </table>
    </div>
    <div class="section">
      <div class="section-title">Amount Breakdown</div>
      <table class="amount-table">${amountRows}</table>
    </div>
  </div>
  <div class="footer">
    This is a computer-generated receipt and does not require a signature.<br>
    <span class="generated">Generated on ${esc(generatedAt)}</span>
  </div>
</div></div>`;
  };

  const handleOpenReceiptPdf = async (paymentId) => {
    try {
      const response = await api.get(`fees/receipt/${paymentId}`);
      const payment = response?.data?.data;
      if (!payment) {
        toast({ title: 'Error', description: 'Receipt data not found', variant: 'destructive' });
        return;
      }
      const receiptNo = (payment.receipt_no || 'receipt').replace(/[^a-zA-Z0-9_-]/g, '');
      const filename = `receipt-${receiptNo}.pdf`;

      toast({ title: 'Generating PDF...', description: 'Your receipt is being prepared.' });

      const html2pdf = await getHtml2pdf();
      const wrapper = document.createElement('div');
      wrapper.id = 'receipt-pdf-temp';
      // Must be in viewport so browser paints it. Width = A5 content (~500px) so receipt covers full half A4.
      wrapper.style.cssText = `position:fixed;left:0;top:0;width:524px;min-height:560px;background:#fff;z-index:99999;opacity:0.01;pointer-events:none;`;
      wrapper.innerHTML = buildReceiptBodyContent(payment);
      document.body.appendChild(wrapper);

      // Wait for layout, paint and (if logo) image load so html2canvas captures content
      const waitMs = payment.school_logo ? 450 : 150;
      await new Promise((r) => setTimeout(r, waitMs));
      const targetEl = wrapper.querySelector('.receipt-pdf-wrap') || wrapper;

      await html2pdf()
        .set({
          filename,
          margin: 8,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a5', orientation: 'portrait' }, // A5 = half A4 (148 x 210 mm) – content fills full width
        })
        .from(targetEl)
        .save();

      wrapper.remove();
      toast({ title: 'Downloaded', description: `${filename} has been saved.` });
    } catch (error) {
      const temp = document.getElementById('receipt-pdf-temp');
      if (temp) temp.remove();
      const msg = error?.message ?? error?.response?.data?.message ?? 'Download failed. Try allowing pop-ups and use Print → Save as PDF.';
      toast({ title: 'Download failed', description: msg, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-600 border-t-blue-500" />
      </div>
    );
  }

  if (!profile) return null;

  const { student, fee_payments = [], fee_summary = {}, attendance_summary = {} } = profile;
  const fullName = [student.first_name, student.last_name].filter(Boolean).join(' ') || 'Student';
  const initials = (student.first_name?.[0] || '') + (student.last_name?.[0] || '') || '?';
  const avatarUrl = getProfileAssetUrl(student.avatar);

  const handlePhotoClick = () => {
    if (uploadingPhoto) return;
    photoInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image (JPG, PNG, GIF, WebP).', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await api.post(`students/${id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newAvatar = res.data?.data?.avatar;
      if (newAvatar) {
        setProfile((prev) => prev ? { ...prev, student: { ...prev.student, avatar: newAvatar } } : null);
        toast({ title: 'Photo updated', description: 'Profile photo has been saved.' });
      }
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.message || 'Could not upload photo.',
        variant: 'destructive',
      });
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="student-profile-page">
      {/* Back + Edit (when viewing) */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Button
          variant="ghost"
          className="text-slate-400 hover:text-white -ml-2"
          onClick={() => navigate(editMode ? `/students/${id}` : '/students')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {editMode ? 'Cancel (view profile)' : 'Back to Students'}
        </Button>
        {!editMode && (
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                className="border-amber-600 text-amber-400 hover:bg-amber-600 hover:text-white"
                onClick={handleResendCredentials}
                disabled={resendingCreds}
              >
                <Send className="w-4 h-4 mr-2" /> {resendingCreds ? 'Sending...' : 'Resend Credentials'}
              </Button>
            )}
            <Button
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              onClick={() => navigate(`/students/${id}/edit`)}
            >
              <Edit className="w-4 h-4 mr-2" /> Edit profile
            </Button>
          </div>
        )}
      </div>

      {/* Header card: photo + main details (or editable when editMode) */}
      <Card className="bg-slate-800 border-slate-700 overflow-hidden">
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 h-24" />
        <CardContent className="relative px-6 pb-6">
          <div className="flex flex-col sm:flex-row gap-6 -mt-14">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handlePhotoChange}
            />
            <button
              type="button"
              onClick={handlePhotoClick}
              disabled={uploadingPhoto}
              className="relative h-28 w-28 shrink-0 rounded-xl border-4 border-slate-800 shadow-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800 hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              <Avatar className="h-full w-full rounded-xl">
                <AvatarImage src={avatarUrl} alt={fullName} className="object-cover" />
                <AvatarFallback className="rounded-xl bg-blue-600/80 text-xl text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-xl">
                <Camera className="w-8 h-8 text-white" />
              </span>
              {uploadingPhoto && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl">
                  <span className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                </span>
              )}
            </button>
            <div className="flex-1 pt-2">
              {editMode && formData ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-400">First name</Label>
                      <Input
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400">Last name</Label>
                      <Input
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="bg-slate-700 text-slate-300">
                      {student.admission_no} (read-only)
                    </Badge>
                    {student.email && (
                      <span className="text-sm text-slate-500 flex items-center gap-1">
                        <Mail className="w-4 h-4" /> {student.email}
                      </span>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-400">Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold text-white">{fullName}</h1>
                    <Badge variant="secondary" className="bg-slate-700 text-slate-300">
                      {student.admission_no}
                    </Badge>
                    {student.status && (
                      <Badge variant={student.status === 'active' ? 'default' : 'outline'}>
                        {student.status}
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-400 mt-1">
                    {student.class_name || '—'} {student.section_name ? `• ${student.section_name}` : ''}
                    {student.roll_no && ` • Roll ${student.roll_no}`}
                  </p>
                  <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-400">
                    {student.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4 text-slate-500" /> {student.email}
                      </span>
                    )}
                    {student.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4 text-slate-500" /> {student.phone}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Personal details (editable form when editMode) */}
        <div className="lg:col-span-2 space-y-6">
          {editMode && formData ? (
            <form onSubmit={handleSave} className="space-y-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <User className="w-5 h-5" /> Edit Personal Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400">Class</Label>
                      <Select value={formData.class_id} onValueChange={(v) => setFormData({ ...formData, class_id: v, section_id: '' })}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          {classes.map((c) => (
                            <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-400">Section</Label>
                      <Select value={formData.section_id} onValueChange={(v) => setFormData({ ...formData, section_id: v })} disabled={!formData.class_id}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue placeholder="Select section" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          {sections.map((s) => (
                            <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400">Roll no</Label>
                      <Input value={formData.roll_no} onChange={(e) => setFormData({ ...formData, roll_no: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-slate-400">Date of birth</Label>
                      <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-400">Admission date</Label>
                    <Input type="date" value={formData.admission_date} onChange={(e) => setFormData({ ...formData, admission_date: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400">Gender</Label>
                      <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-400">Blood group</Label>
                      <Input value={formData.blood_group} onChange={(e) => setFormData({ ...formData, blood_group: e.target.value })} className="bg-slate-700 border-slate-600 text-white" placeholder="e.g. A+" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-400">Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-400">Address</Label>
                    <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="bg-slate-700 border-slate-600 text-white" placeholder="Street address" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-400">City</Label>
                      <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-slate-400">State</Label>
                      <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-slate-400">Pincode</Label>
                      <Input value={formData.pincode} onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <User className="w-5 h-5" /> Parent / Guardian Details
                  </CardTitle>
                  <p className="text-slate-400 text-sm font-normal">Contact details for school to reach parents or guardian.</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-400">Parent / Guardian name</Label>
                    <Input value={formData.guardian_name} onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })} className="bg-slate-700 border-slate-600 text-white" placeholder="Full name" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400">Phone</Label>
                      <Input value={formData.guardian_phone} onChange={(e) => setFormData({ ...formData, guardian_phone: e.target.value })} className="bg-slate-700 border-slate-600 text-white" placeholder="Contact number" />
                    </div>
                    <div>
                      <Label className="text-slate-400">Email</Label>
                      <Input type="email" value={formData.guardian_email} onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })} className="bg-slate-700 border-slate-600 text-white" placeholder="Email address" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <div className="flex gap-3">
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  {saving ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? 'Saving...' : 'Save changes'}
                </Button>
                <Button type="button" variant="outline" className="border-slate-600 text-slate-300" onClick={() => navigate(`/students/${id}`)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5" /> Personal Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <DetailRow label="Date of birth" value={formatDate(student.date_of_birth)} />
              <DetailRow label="Gender" value={student.gender || '—'} />
              <DetailRow label="Blood group" value={student.blood_group || '—'} />
              <DetailRow label="Religion" value={student.religion || '—'} />
              <DetailRow label="Nationality" value={student.nationality || '—'} />
              <DetailRow label="Admission date" value={formatDate(student.admission_date)} />
              {student.address && (
                <div className="flex gap-2 pt-1">
                  <MapPin className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                  <span className="text-slate-300">
                    {[student.address, student.city, student.state, student.pincode].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
              {student.previous_school && (
                <DetailRow label="Previous school" value={student.previous_school} />
              )}
            </CardContent>
          </Card>
          )}

          {/* Parent / Guardian (view mode) – always show on profile page */}
          {!editMode && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5" /> Parent / Guardian
                </CardTitle>
                <p className="text-slate-400 text-sm font-normal">Contact details for school to reach parents or guardian.</p>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <DetailRow label="Name" value={student.guardian_name || '—'} />
                <div className="flex items-center gap-2 text-slate-300">
                  <Phone className="w-4 h-4 text-slate-500 shrink-0" />
                  <span>{student.guardian_phone || '—'}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Mail className="w-4 h-4 text-slate-500 shrink-0" />
                  <span>{student.guardian_email || '—'}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fee payments list */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5" /> Fee Payments
                </CardTitle>
                {fee_payments.length > 0 && (
                  <Badge className="bg-green-600/20 text-green-400 border-green-600/50 text-sm px-3 py-1">
                    {fee_payments.filter(p => p.status === 'paid').length} Paid
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mt-2">
                <p className="text-slate-400 text-sm">
                  Total paid: <span className="text-white font-semibold">{formatCurrency(fee_summary.total_paid)}</span>
                </p>
                <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">
                  {fee_summary.payment_count || 0} payment(s)
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {fee_payments.length === 0 ? (
                <p className="text-slate-500 text-sm py-4">No fee payments recorded yet.</p>
              ) : (
                <div className="rounded-lg border border-slate-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-700/50 text-slate-300 text-left">
                        <th className="px-4 py-2 font-medium">Receipt</th>
                        <th className="px-4 py-2 font-medium">Fee Month</th>
                        <th className="px-4 py-2 font-medium">Date</th>
                        <th className="px-4 py-2 font-medium">Amount</th>
                        <th className="px-4 py-2 font-medium">Method</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fee_payments.map((p) => {
                        let feeMonthLabel = '—';
                        if (p.fee_month) {
                          try {
                            feeMonthLabel = new Date(p.fee_month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                          } catch (e) {
                            feeMonthLabel = p.fee_month;
                          }
                        } else if (p.payment_date) {
                          try {
                            feeMonthLabel = new Date(p.payment_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                          } catch (e) {
                            feeMonthLabel = '—';
                          }
                        }
                        return (
                          <tr key={p.id} className="border-t border-slate-700 text-slate-300">
                            <td className="px-4 py-2">
                              <button
                                type="button"
                                onClick={() => handleOpenReceiptPdf(p.id)}
                                className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 hover:underline focus:outline-none focus:underline"
                                title="View receipt PDF"
                              >
                                <FileText className="w-4 h-4 shrink-0" />
                                {p.receipt_no || '—'}
                              </button>
                            </td>
                            <td className="px-4 py-2 font-medium text-blue-300">{feeMonthLabel}</td>
                            <td className="px-4 py-2">{formatDate(p.payment_date)}</td>
                            <td className="px-4 py-2 font-medium text-white">
                              {formatCurrency(p.total_amount ?? p.amount)}
                            </td>
                            <td className="px-4 py-2 capitalize">{p.payment_method || '—'}</td>
                            <td className="px-4 py-2">
                              {p.status === 'paid' ? (
                                <Badge className="bg-green-600/20 text-green-400 border-green-600/50 text-xs px-2 py-0.5">
                                  Paid
                                </Badge>
                              ) : p.status === 'pending' ? (
                                <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-600/50 text-xs px-2 py-0.5">
                                  Pending
                                </Badge>
                              ) : p.status === 'cancelled' ? (
                                <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-xs px-2 py-0.5">
                                  Cancelled
                                </Badge>
                              ) : (
                                <Badge className="bg-slate-600/20 text-slate-400 border-slate-600/50 text-xs px-2 py-0.5">
                                  {p.status || '—'}
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fee by month (due 28th, overdue after 5th next month) */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5" /> Fee by month
                </CardTitle>
                {feeMonths && feeMonths.length > 0 && (
                  <div className="flex items-center gap-2">
                    {feeMonths.filter(m => m.status === 'paid').length > 0 && (
                      <Badge className="bg-green-600/20 text-green-400 border-green-600/50 text-xs px-2 py-1">
                        {feeMonths.filter(m => m.status === 'paid').length} Paid
                      </Badge>
                    )}
                    {feeMonths.filter(m => m.status === 'overdue').length > 0 && (
                      <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-xs px-2 py-1">
                        {feeMonths.filter(m => m.status === 'overdue').length} Overdue
                      </Badge>
                    )}
                    {feeMonths.filter(m => m.status === 'due').length > 0 && (
                      <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-600/50 text-xs px-2 py-1">
                        {feeMonths.filter(m => m.status === 'due').length} Due
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 mt-2">
                <p className="text-slate-400 text-sm">
                  Due date: 28th of each month. Overdue if not paid by 5th of next month (compared with admission date).
                </p>
                {feeMonths && feeMonths.length > 0 && (
                  <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">
                    {feeMonths.length} month(s)
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {feeMonths === null ? (
                <div className="flex justify-center py-6">
                  <span className="animate-spin rounded-full h-6 w-6 border-2 border-slate-600 border-t-blue-500" />
                </div>
              ) : feeMonths.length === 0 ? (
                <p className="text-slate-500 text-sm py-4">No fee months data.</p>
              ) : (
                <div className="rounded-lg border border-slate-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-700/50 text-slate-300 text-left">
                        <th className="px-4 py-2 font-medium">Month</th>
                        <th className="px-4 py-2 font-medium">Due date (28th)</th>
                        <th className="px-4 py-2 font-medium">Overdue from</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feeMonths.map((m) => (
                        <tr
                          key={m.year_month}
                          className={`border-t border-slate-700 text-slate-300 ${m.status === 'overdue' || m.status === 'due' ? 'hover:bg-slate-700/30 cursor-pointer' : ''}`}
                          onClick={() => {
                            if (m.status === 'overdue' || m.status === 'due') {
                              handleOpenPayFee(m);
                            }
                          }}
                        >
                          <td className="px-4 py-2 font-medium">{m.month_label}</td>
                          <td className="px-4 py-2">{m.due_date ? formatDate(m.due_date) : '—'}</td>
                          <td className="px-4 py-2">{m.overdue_from ? formatDate(m.overdue_from) : '—'}</td>
                          <td className="px-4 py-2">
                            {m.status === 'paid' ? (
                              <Badge className="bg-green-600/20 text-green-400 border-green-600/50 text-xs px-2 py-0.5">
                                Paid
                              </Badge>
                            ) : m.status === 'overdue' ? (
                              <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-xs px-2 py-0.5">
                                Overdue
                              </Badge>
                            ) : m.status === 'due' ? (
                              <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-600/50 text-xs px-2 py-0.5">
                                Due
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-600/20 text-slate-400 border-slate-600/50 text-xs px-2 py-0.5">
                                Not Due
                              </Badge>
                            )}
                            {(m.status === 'overdue' || m.status === 'due') && (
                              <span className="ml-2 text-xs text-blue-400">(Click to pay)</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Attendance & quick stats */}
        <div className="space-y-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <ClipboardList className="w-5 h-5" /> Attendance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-400">Average attendance</span>
                  <span className="text-white font-semibold">
                    {attendance_summary.average_percent ?? 0}%
                  </span>
                </div>
                <Progress value={attendance_summary.average_percent ?? 0} className="h-2" />
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <StatPill label="Present" value={attendance_summary.present ?? 0} />
                <StatPill label="Absent" value={attendance_summary.absent ?? 0} />
                <StatPill label="Late" value={attendance_summary.late ?? 0} />
                <StatPill label="Total days" value={attendance_summary.total_days ?? 0} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <IndianRupee className="w-5 h-5" /> Fee Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-white">
                {formatCurrency(fee_summary.total_paid)}
              </p>
              <p className="text-slate-400 text-sm mt-1">
                {fee_summary.payment_count ?? 0} payment(s) recorded
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Pay Fee Dialog (opened from overdue/due rows) */}
      <Dialog open={payFeeDialogOpen} onOpenChange={setPayFeeDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Collect Fee - {payFeeForm.month_label}</DialogTitle>
            <p className="text-slate-400 text-sm font-normal">Pay fee for {student?.first_name} {student?.last_name}</p>
          </DialogHeader>
          <form onSubmit={handlePayFee} className="space-y-4">
            {feeStructure.length > 0 && (
              <div className="bg-slate-700/50 rounded-lg p-3 text-sm">
                <p className="text-slate-300 font-medium mb-1">Monthly fee breakdown:</p>
                <ul className="space-y-1 text-slate-400">
                  {feeStructure
                    .filter((fs) => fs.fee_type_frequency === 'monthly')
                    .map((fs) => (
                      <li key={fs.id} className="flex justify-between">
                        <span>{fs.fee_type_name}</span>
                        <span>₹{Number(fs.amount || 0).toLocaleString()}</span>
                      </li>
                    ))}
                  {feeStructure.filter((fs) => fs.fee_type_frequency === 'monthly').length === 0 && (
                    <li className="text-slate-500">No monthly fees defined for this class</li>
                  )}
                </ul>
                {feeStructure.filter((fs) => fs.fee_type_frequency === 'monthly').length > 0 && (
                  <p className="text-slate-300 font-semibold mt-2 pt-2 border-t border-slate-600 flex justify-between">
                    <span>Total monthly fee:</span>
                    <span>₹{calculateMonthlyFeeAmount().toLocaleString()}</span>
                  </p>
                )}
              </div>
            )}
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={payFeeForm.amount}
                onChange={(e) => setPayFeeForm({ ...payFeeForm, amount: e.target.value })}
                className="bg-slate-700 border-slate-600"
                required
                placeholder={feeStructure.length === 0 ? "Enter amount (fee structure not set for this class)" : ""}
              />
              {feeStructure.length === 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  No fee structure found for this class. Set fees in Fee Management → Fee Structure by Class.
                </p>
              )}
            </div>
            <div>
              <Label>Discount (₹)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={payFeeForm.discount}
                onChange={(e) => setPayFeeForm({ ...payFeeForm, discount: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select
                value={payFeeForm.payment_method}
                onValueChange={(v) => setPayFeeForm({ ...payFeeForm, payment_method: v })}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Date *</Label>
              <Input 
                type="date" 
                value={payFeeForm.payment_date || new Date().toISOString().split('T')[0]} 
                onChange={(e) => setPayFeeForm({ ...payFeeForm, payment_date: e.target.value })}
                className="bg-slate-700 border-slate-600" 
                required
                max={new Date().toISOString().split('T')[0]}
              />
              <p className="text-slate-400 text-xs mt-1">
                Select the date when the fee was actually paid. Defaults to today. You can backdate for late entries.
              </p>
            </div>
            <div>
              <Label>Remarks</Label>
              <Input
                value={payFeeForm.remarks}
                onChange={(e) => setPayFeeForm({ ...payFeeForm, remarks: e.target.value })}
                className="bg-slate-700 border-slate-600"
                placeholder="Optional notes"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setPayFeeDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Collect Fee
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-300">{value}</span>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="bg-slate-700/50 rounded-lg px-3 py-2">
      <p className="text-slate-400 text-xs">{label}</p>
      <p className="text-white font-semibold">{value}</p>
    </div>
  );
}

export default StudentProfile;
